import React, { useEffect, useState } from 'react';
import { X, CheckCircle, AlertCircle, Info } from 'lucide-react';

const Toast = ({ id, type, message, action, duration = 3000, onClose }) => {
    const [isExiting, setIsExiting] = useState(false);

    useEffect(() => {
        const exitTimer = setTimeout(() => {
            setIsExiting(true);
        }, duration - 200); // Start exit animation 200ms before removal

        const removeTimer = setTimeout(() => {
            onClose(id);
        }, duration);

        return () => {
            clearTimeout(exitTimer);
            clearTimeout(removeTimer);
        };
    }, [id, duration, onClose]);

    const icons = {
        success: <CheckCircle size={20} className="text-green-400" />,
        error: <AlertCircle size={20} className="text-red-400" />,
        info: <Info size={20} className="text-blue-400" />
    };

    const bgColors = {
        success: 'bg-green-900/80 border-green-700',
        error: 'bg-red-900/80 border-red-700',
        info: 'bg-blue-900/80 border-blue-700'
    };

    return (
        <div className={`flex items-center justify-between gap-4 p-3 bg-[#252526] rounded-lg border border-[#3c3c3c] max-w-md transition-all duration-200 ${bgColors[type] || bgColors.info} ${isExiting
            ? 'opacity-0 -translate-y-2 scale-95'
            : 'opacity-100 translate-y-0 scale-100 animate-in slide-in-from-top'
            }`}>
            <div className={`flex-shrink-0 mt-0.5 ${action ? 'self-start' : ''}`}>
                {icons[type] || icons.info}
            </div>
            <div className="flex-1">
                <p className="text-sm font-medium text-[#cccccc]">{message}</p>
            </div>

            {action && (
                <button
                    onClick={() => {
                        action.onClick();
                        onClose(id); // Close toast immediately on action
                    }}
                    className="flex-shrink-0 px-3 py-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-500 rounded transition-colors uppercase tracking-wider"
                >
                    {action.label}
                </button>
            )}

            <button onClick={() => onClose(id)} className={`flex-shrink-0 text-[#808080] hover:text-gray-300 transition-colors ${action ? 'self-start' : ''}`}>
                <X size={16} />
            </button>
        </div>
    );
};

import { createPortal } from 'react-dom';

export const ToastContainer = ({ toasts, removeToast }) => {
    return createPortal(
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[10000] flex flex-col gap-2 pointer-events-none items-center">
            <div className="pointer-events-auto flex flex-col gap-2 items-center">
                {toasts.map(toast => (
                    <Toast key={toast.id} {...toast} onClose={removeToast} />
                ))}
            </div>
        </div>,
        document.body
    );
};
